import { useTable } from "@refinedev/antd";
import { List } from "@refinedev/antd";
import { Alert, Badge, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { FC } from "react";
import type { Deployment } from "../../types";

dayjs.extend(relativeTime);

const { Text } = Typography;

const ENV_COLOR: Record<string, string> = {
  production: "red",
  staging: "orange",
  local: "blue",
};

export const DeploymentList: FC = () => {
  const { tableProps } = useTable<Deployment>({ syncWithLocation: false });

  return (
    <List title="Deployments" canCreate={false}>
      <Alert
        type="info"
        showIcon
        message="Pro feature (free during alpha)"
        description="Deploy history will be reserved for Pro and Agency plans. Enjoy it for free while Loopress is in alpha."
        style={{ marginBottom: 16 }}
      />
      <Table
        {...tableProps}
        rowKey="id"
        locale={{
          emptyText: (
            <div style={{ padding: "32px 0" }}>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>No deploys yet</p>
              <p style={{ color: "#888", margin: 0 }}>
                Runs of <Text code>lps push</Text> will appear here automatically.
              </p>
            </div>
          ),
        }}
      >
        <Table.Column<Deployment>
          title="Project"
          render={(_, record) =>
            record.projectName ?? null
          }
        />
        <Table.Column<Deployment>
          title="Environment"
          render={(_, record) => (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {record.environmentName && (
                <Tag color={ENV_COLOR[record.environmentName] ?? "default"} style={{ marginRight: 0 }}>
                  {record.environmentName}
                </Tag>
              )}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.url}
              </Text>
            </span>
          )}
        />
        <Table.Column<Deployment>
          title="Status"
          dataIndex="status"
          render={(v: "success" | "failure") =>
            v === "success" ? (
              <Badge status="success" text="Success" />
            ) : (
              <Badge status="error" text="Failed" />
            )
          }
        />
        <Table.Column<Deployment>
          title="Snippets"
          dataIndex="snippetCount"
          render={(v: number | null) =>
            v ?? null
          }
        />
        <Table.Column<Deployment>
          title="Token"
          dataIndex="tokenLabel"
        />
        <Table.Column<Deployment>
          title="When"
          dataIndex="pushedAt"
          render={(v: string) => dayjs(v).fromNow()}
        />
      </Table>
    </List>
  );
};
